import { Subject } from 'rxjs';

import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef, Input, OnDestroy } from '@angular/core';

export interface IBreadcrumb {
  text: string;
  link?: string;
  collapsedText?: string;
  hide?: boolean;
  disabled?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'collapsible-breadcrumb',
  templateUrl: './collapsible-breadcrumb.component.html',
  styleUrls: ['./collapsible-breadcrumb.component.scss'],
})
export class CollapsibleBreadcrumbComponent implements AfterViewInit, OnDestroy {
  @Input() public breadcrumbs: IBreadcrumb[] = [];
  public disabledAllGroups: boolean = true;

  private el: HTMLElement;
  private unsubscribe: Subject<void> = new Subject<void>();
  private resizeObserver: ResizeObserver = new ResizeObserver(
    this.onWindowResize.bind(this),
  );
  private get breadcrumbEl(): Element {
    return this.el.querySelector('nz-breadcrumb');
  }

  constructor(
    elRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    this.el = elRef.nativeElement;
  }

  public ngAfterViewInit(): void {
    this.onWindowResize();
    this.resizeObserver.observe(this.breadcrumbEl);
  }

  public ngOnDestroy(): void {
    this.resizeObserver
      .unobserve(this.breadcrumbEl);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public onWindowResize(): void {
    this.updateBreadcrumbs();
  }

  private getBreadcrumbOverflowing(): boolean {
    this.cdr.detectChanges();
    const breadcrumbElements: Element[] = Array.from(this.breadcrumbEl.children);
    let firstChildTop: number = null;
    let firstChildBottom: number = null;
    let allChildVerticalBoundsEqual: boolean = true;
    breadcrumbElements.forEach(child => {
      const boundingClientRect: DOMRect = child.getBoundingClientRect();
      const childTop: number = boundingClientRect.top;
      const childBottom: number = boundingClientRect.bottom;
      if (firstChildTop === null) {
        firstChildTop = childTop;
        firstChildBottom = childBottom;
      }
      allChildVerticalBoundsEqual = allChildVerticalBoundsEqual
        && childTop === firstChildTop && childBottom === firstChildBottom;
    });

    return !allChildVerticalBoundsEqual;
  }

  private updateBreadcrumbs(): void {
    this.breadcrumbs.forEach(breadcrumb => {
      delete breadcrumb.collapsedText;
      breadcrumb.hide = false;
    });

    for (let i = 0; this.getBreadcrumbOverflowing() && i < this.breadcrumbs.length - 1; i++) {
      this.breadcrumbs[i].collapsedText = '...';
    }

    if (this.getBreadcrumbOverflowing()) {
      this.breadcrumbs.slice(0, this.breadcrumbs.length - 2).forEach(breadcrumb => breadcrumb.hide = true);
    }

    const lastBreadcrumbText: string = this.breadcrumbs[this.breadcrumbs.length - 1].text;
    for (let i = lastBreadcrumbText.length - 4; this.getBreadcrumbOverflowing() && i > 1; i--) {
      this.breadcrumbs[this.breadcrumbs.length - 1].collapsedText = lastBreadcrumbText.slice(0, i) + '...';
    }
  }
}
